import {
    SeaEmissionsAggregatedByCountryLaneAndCarrier,
    SeaEmissionsAggregatedByRouteAndCarrier,
    SeaShippingMetrics,
    SeaShippingRecommendations,
} from '@lune-fe/dapi-models'
import {
    formatNumbers,
    LuneThemeNew,
    MainLayoutContainer,
    Text,
    Tooltip,
} from '@lune-fe/lune-ui-lib'
import CircleIcon from '@mui/icons-material/Circle'
import { Box, Stack } from '@mui/material'
import moment from 'moment'
import { useRef, useState } from 'react'

import Divider from 'components/Divider'
import { CountryLaneAndCarrierMetrics } from 'views/ReduceEmissions/CountryLaneAndCarrierMetrics'
import { RecommendationsBox } from 'views/ReduceEmissions/RecommendationsBox'
import {
    RouterAndCarrierMetrics,
    SortByOption,
} from 'views/ReduceEmissions/RouterAndCarrierMetrics'
const { palette } = LuneThemeNew

const HeadlineMetric = ({ label, value }: { label: string; value: string }) => {
    return (
        <Stack gap={1}>
            <Text variant={'body3'}>{label}</Text>
            <Text variant={'h5'}>{value}</Text>
        </Stack>
    )
}

export const EnabledView = ({
    data,
    preview,
}: {
    data: {
        seaShippingMetrics: SeaShippingMetrics
        routerAndCarrierMetrics: SeaEmissionsAggregatedByRouteAndCarrier
        countryLaneAndCarrierMetrics: SeaEmissionsAggregatedByCountryLaneAndCarrier
        recommendations: SeaShippingRecommendations
    }
    preview: boolean
}) => {
    const {
        seaShippingMetrics,
        routerAndCarrierMetrics,
        countryLaneAndCarrierMetrics,
        recommendations,
    } = data

    const [preselectedCarrier, setPreSelectedCarrier] = useState<string | undefined>()
    const [preselectedPortPairs, setPreselectedPortPairs] = useState<[string, string] | undefined>()
    const [activeSortOption, setActiveSortOption] = useState<SortByOption>(
        SortByOption.TOTAL_EMISSIONS_DESC,
    )
    const scrollToRef = useRef<HTMLDivElement>(null)

    return (
        <Box>
            {preview && (
                <Box
                    sx={{
                        position: 'sticky',
                        top: 0,
                        left: 0,
                        right: 0,
                        background: palette.BlackTransparent20,
                        color: 'red',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        p: 1,
                    }}
                >
                    <Text variant={'button'}>
                        PREVIEW - PREVIEW - PREVIEW - PREVIEW - PREVIEW - PREVIEW - PREVIEW -
                        PREVIEW - PREVIEW - PREVIEW - PREVIEW - PREVIEW - PREVIEW - PREVIEW -
                        PREVIEW - PREVIEW - PREVIEW - PREVIEW - PREVIEW - PREVIEW - PREVIEW -
                        PREVIEW - PREVIEW - PREVIEW - PREVIEW{' '}
                    </Text>
                </Box>
            )}
            <MainLayoutContainer
                headerComponent={
                    <>
                        <Text variant={'h4'} sx={{ mb: 2 }}>
                            Emission Reductions
                        </Text>

                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                            <Tooltip
                                title={`Sea shipping accounts for ${((seaShippingMetrics.totalSeaShippingLegs / seaShippingMetrics.totalShippingLegs) * 100).toFixed(1)}% of your shipping activity.`}
                            >
                                <Text
                                    variant="body3"
                                    sx={{
                                        color: 'Grey700',
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                    }}
                                >
                                    {formatNumbers(seaShippingMetrics.totalSeaShippingLegs)} sea
                                    shipments
                                </Text>
                            </Tooltip>
                            <CircleIcon sx={{ color: 'Grey700', fontSize: '4px' }} />
                            <Text
                                variant="body3"
                                sx={{
                                    color: 'Grey700',
                                }}
                            >
                                Data from previous 12 months from{' '}
                                {moment.utc().format('MMMM D, YYYY')}
                            </Text>
                        </Box>
                    </>
                }
            >
                <Stack
                    direction="column"
                    sx={{
                        mb: 9,
                        width: '100%',
                    }}
                    spacing={{
                        xs: 9,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <HeadlineMetric
                            label="TEUs shipped"
                            value={`${seaShippingMetrics.totalTeuSea ? formatNumbers(seaShippingMetrics.totalTeuSea, 2) : 0}`}
                        />
                        <HeadlineMetric
                            label="Shipment emissions"
                            value={`${seaShippingMetrics.totalEmissionsSea ? formatNumbers(seaShippingMetrics.totalEmissionsSea, 2) : 0} tCO₂e`}
                        />
                        <HeadlineMetric
                            label="Carbon intensity (per TEU·km)"
                            value={`${seaShippingMetrics.carbonIntensitySea ? formatNumbers(seaShippingMetrics.carbonIntensitySea, 2) : 0} gCO₂e`}
                        />
                    </Box>
                    {!!recommendations.recommendations.length && (
                        <>
                            <Divider />
                            <RecommendationsBox
                                recommendations={data.recommendations}
                                onClick={(recommendation) => {
                                    setPreselectedPortPairs([
                                        recommendation.sourcePort,
                                        recommendation.destinationPort,
                                    ])
                                    setPreSelectedCarrier(recommendation.carrier)
                                    scrollToRef.current?.scrollIntoView({
                                        behavior: 'smooth',
                                        inline: 'center',
                                        block: 'nearest',
                                    })
                                    setActiveSortOption(SortByOption.CARRIER_INTENSITY_DESC)
                                }}
                            />
                        </>
                    )}
                    <Box ref={scrollToRef}>
                        <Stack
                            direction="column"
                            sx={{
                                width: '100%',
                            }}
                            spacing={{
                                xs: 9,
                            }}
                        >
                            {!!routerAndCarrierMetrics.aggregatedRoutes.emissionsByCarrier
                                .length && (
                                <>
                                    <Divider />
                                    <RouterAndCarrierMetrics
                                        preselectedPortPair={preselectedPortPairs}
                                        onSelectChange={() => setPreSelectedCarrier(undefined)}
                                        sortOptions={{ setActiveSortOption, activeSortOption }}
                                        data={routerAndCarrierMetrics}
                                    />
                                </>
                            )}
                            {!!countryLaneAndCarrierMetrics.aggregatedCarriers
                                .emissionsByCountryLane.length && (
                                <>
                                    <Divider />
                                    <CountryLaneAndCarrierMetrics
                                        data={countryLaneAndCarrierMetrics}
                                        onSelectChange={() => setPreSelectedCarrier(undefined)}
                                        preselectedCarrier={preselectedCarrier}
                                    />
                                </>
                            )}
                        </Stack>
                    </Box>
                </Stack>
            </MainLayoutContainer>
        </Box>
    )
}
